import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GraftonChat } from "@/components/GraftonChat";
import {
  FileText, Receipt, RefreshCw, Building2, Euro,
  TrendingUp, TrendingDown, Loader2, ArrowUpDown,
  CheckCircle2, Clock, AlertTriangle,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

interface Invoice {
  id: string;
  number: string;
  title: string;
  total: number;
  status: string;
  date: string;
  due_date: string;
  company_name: string;
  source: string;
}

interface Transaction {
  id: string;
  amount: number;
  currency: string;
  side: string;
  label: string;
  status: string;
  operation_type: string;
  settled_at: string;
  category: string;
}

const statusBadge = (status: string) => {
  const lower = (status || "").toLowerCase();
  if (lower.includes("paid") || lower.includes("payé")) return <Badge variant="default">Payée</Badge>;
  if (lower.includes("late") || lower.includes("retard")) return <Badge variant="destructive">En retard</Badge>;
  if (lower.includes("sent") || lower.includes("envoyé")) return <Badge variant="outline">Envoyée</Badge>;
  if (lower.includes("draft") || lower.includes("brouillon")) return <Badge variant="secondary">Brouillon</Badge>;
  return <Badge variant="secondary">{status || "—"}</Badge>;
};

const formatCurrency = (n: number) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n);
const formatDate = (d: string) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "—";

export default function AccountingDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [qontoBalance, setQontoBalance] = useState<number | null>(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
  }, [user, loading, navigate]);

  const fetchData = useCallback(async () => {
    setDataLoading(true);
    try {
      const [invRes, txRes, balRes] = await Promise.all([
        fetch(`${API_URL}/api/axonaut/invoices`, { credentials: "include" }),
        fetch(`${API_URL}/api/qonto/transactions?per_page=30`, { credentials: "include" }),
        fetch(`${API_URL}/api/qonto/balance`, { credentials: "include" }),
      ]);
      if (invRes.ok) { const d = await invRes.json(); setInvoices(d.invoices || []); }
      if (txRes.ok) { const d = await txRes.json(); setTransactions(d.transactions || []); }
      if (balRes.ok) { const d = await balRes.json(); setQontoBalance(d.total_balance); }
    } catch {}
    setDataLoading(false);
  }, []);

  useEffect(() => { if (user) fetchData(); }, [user, fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  if (loading || dataLoading) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full bg-background">
          <DashboardSidebar />
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </SidebarProvider>
    );
  }

  // Compute KPIs
  const totalInvoiced = invoices.reduce((s, i) => s + (i.total || 0), 0);
  const totalDebit = transactions.filter(t => t.side === "debit").reduce((s, t) => s + t.amount, 0);
  const totalCredit = transactions.filter(t => t.side === "credit").reduce((s, t) => s + t.amount, 0);
  const unpaidInvoices = invoices.filter(i => {
    const s = (i.status || "").toLowerCase();
    return !s.includes("paid") && !s.includes("payé");
  });

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <DashboardSidebar />
        <main className="flex-1 overflow-y-auto">
          {/* Header */}
          <div className="border-b border-border/50 bg-card/30 backdrop-blur px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Receipt className="h-5 w-5 text-primary" />
                  Comptabilité
                </h1>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Factures Axonaut et transactions Qonto en temps réel
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="gap-1.5 text-[10px]">
                  <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Axonaut
                </Badge>
                <Badge variant="outline" className="gap-1.5 text-[10px]">
                  <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Qonto
                </Badge>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={handleRefresh} disabled={refreshing} data-testid="accounting-refresh">
                  <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
                  Actualiser
                </Button>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Solde Qonto", value: qontoBalance !== null ? formatCurrency(qontoBalance) : "—", icon: Euro, color: "text-emerald-500", sub: "Tous comptes" },
                { label: "Facturé (Axonaut)", value: formatCurrency(totalInvoiced), icon: FileText, color: "text-primary", sub: `${invoices.length} facture(s)` },
                { label: "Entrées (Qonto)", value: formatCurrency(totalCredit), icon: TrendingUp, color: "text-emerald-400", sub: `${transactions.filter(t => t.side === "credit").length} opérations` },
                { label: "Sorties (Qonto)", value: formatCurrency(totalDebit), icon: TrendingDown, color: "text-red-400", sub: `${transactions.filter(t => t.side === "debit").length} opérations` },
              ].map((kpi) => (
                <Card key={kpi.label} className="border-border/50" data-testid={`accounting-kpi-${kpi.label.replace(/\s/g, '-').toLowerCase()}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2.5 rounded-xl bg-muted/50 ${kpi.color}`}>
                        <kpi.icon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{kpi.label}</p>
                        <p className="text-lg font-bold">{kpi.value}</p>
                        <p className="text-[10px] text-muted-foreground">{kpi.sub}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Tabs */}
            <Tabs defaultValue="invoices" className="space-y-4">
              <TabsList className="bg-muted/50">
                <TabsTrigger value="invoices" className="gap-1.5 text-xs" data-testid="tab-invoices">
                  <FileText className="h-3.5 w-3.5" /> Factures Axonaut
                </TabsTrigger>
                <TabsTrigger value="transactions" className="gap-1.5 text-xs" data-testid="tab-transactions">
                  <ArrowUpDown className="h-3.5 w-3.5" /> Transactions Qonto
                </TabsTrigger>
              </TabsList>

              {/* Invoices Tab */}
              <TabsContent value="invoices">
                <Card className="border-border/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-primary" />
                      Factures Axonaut
                      <Badge variant="outline" className="text-[9px] ml-2 border-emerald-500/30 text-emerald-400">LIVE</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {invoices.length === 0 ? (
                      <div className="text-center py-12">
                        <FileText className="h-8 w-8 mx-auto text-muted-foreground/20 mb-3" />
                        <p className="text-sm text-muted-foreground">Aucune facture dans Axonaut</p>
                        <p className="text-xs text-muted-foreground/60 mt-1">Les factures créées dans Axonaut apparaîtront ici automatiquement.</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-border/30">
                        {/* Header */}
                        <div className="grid grid-cols-6 gap-4 px-5 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider bg-muted/20">
                          <span>Référence</span>
                          <span>Client</span>
                          <span>Titre</span>
                          <span>Date</span>
                          <span>Montant</span>
                          <span>Statut</span>
                        </div>
                        {invoices.map((inv) => (
                          <div key={inv.id} className="grid grid-cols-6 gap-4 px-5 py-3 hover:bg-muted/10 transition-colors items-center" data-testid={`invoice-${inv.id}`}>
                            <span className="text-sm font-medium text-primary">{inv.number || "—"}</span>
                            <span className="text-sm">{inv.company_name || "—"}</span>
                            <span className="text-sm text-muted-foreground truncate">{inv.title || "—"}</span>
                            <span className="text-xs text-muted-foreground">{formatDate(inv.date)}</span>
                            <span className="text-sm font-bold">{formatCurrency(inv.total)}</span>
                            <span>{statusBadge(inv.status)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Unpaid alert */}
                {unpaidInvoices.length > 0 && (
                  <Card className="mt-4 border-amber-500/20 bg-amber-500/5">
                    <CardContent className="p-4 flex items-center gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />
                      <div>
                        <p className="text-sm font-medium">{unpaidInvoices.length} facture(s) non payée(s)</p>
                        <p className="text-xs text-muted-foreground">
                          Total en attente : {formatCurrency(unpaidInvoices.reduce((s, i) => s + (i.total || 0), 0))}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Transactions Tab */}
              <TabsContent value="transactions">
                <Card className="border-border/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Euro className="h-4 w-4 text-violet-500" />
                      Transactions Qonto
                      <Badge variant="outline" className="text-[9px] ml-2 border-emerald-500/30 text-emerald-400">LIVE</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {transactions.length === 0 ? (
                      <div className="text-center py-12">
                        <ArrowUpDown className="h-8 w-8 mx-auto text-muted-foreground/20 mb-3" />
                        <p className="text-sm text-muted-foreground">Aucune transaction Qonto</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-border/30 max-h-[500px] overflow-auto">
                        {transactions.map((tx, i) => (
                          <div key={tx.id || i} className="flex items-center gap-4 px-5 py-3 hover:bg-muted/10 transition-colors" data-testid={`transaction-${tx.id || i}`}>
                            <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${
                              tx.side === "credit" ? "bg-emerald-500/10" : "bg-red-500/10"
                            }`}>
                              {tx.side === "credit" ? (
                                <TrendingUp className="h-4 w-4 text-emerald-500" />
                              ) : (
                                <TrendingDown className="h-4 w-4 text-red-500" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{tx.label || "Transaction"}</p>
                              <p className="text-[10px] text-muted-foreground">
                                {formatDate(tx.settled_at)} — {tx.operation_type} {tx.category ? `· ${tx.category}` : ""}
                              </p>
                            </div>
                            <p className={`text-sm font-bold tabular-nums ${tx.side === "credit" ? "text-emerald-500" : "text-red-400"}`}>
                              {tx.side === "credit" ? "+" : "-"}{tx.amount?.toFixed(2)} €
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </main>

        <GraftonChat
          context={{ invoices: invoices.length, balance: qontoBalance, transactions: transactions.length }}
          quickPrompts={[
            "Quelles factures sont en retard ?",
            "Résumé des dépenses du mois",
            "Comment optimiser ma trésorerie ?",
          ]}
          subtitle="Expert comptable"
          welcomeMessage="Posez-moi vos questions sur vos factures Axonaut et transactions Qonto."
        />
      </div>
    </SidebarProvider>
  );
}
